import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { StateMessageComponent } from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  AppointmentDispositionItemInfo,
  AppointmentSubDispositionInfo,
  CreateAppointmentDispositionUpdateSubmissionDto,
  UpdateServiceAppointmentDispositionSubmissionDto,
} from '../../sdk/sdk';
import { AppointmentHistoryItem } from '../../models/appointment-history-item.model';
import { DispositionService } from '../../services/disposition.service';

@Component({
  selector: 'app-edit-disposition-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, StateMessageComponent],
  templateUrl: './edit-disposition-modal.component.html',
})
export class EditDispositionModalComponent implements OnInit {
  private readonly dispositionService = inject(DispositionService);
  readonly activeModal = inject(NgbActiveModal);

  appointment?: AppointmentHistoryItem;

  readonly updating = signal(false);
  readonly dispositions = signal<AppointmentDispositionItemInfo[]>([]);
  readonly subDispositions = signal<AppointmentSubDispositionInfo[]>([]);
  readonly loadingDispositions = signal(false);
  readonly loadDispositionsError = signal<string | null>(null);
  readonly updateError = signal<string | null>(null);

  readonly editForm = new FormGroup({
    dispositionId: new FormControl<string>('', [Validators.required]),
    subDispositionId: new FormControl<string>('', [Validators.required]),
  });

  get isServiceRun(): boolean {
    return this.appointment?.isServiceRun ?? false;
  }

  ngOnInit(): void {
    this.fetchDispositions();

    if (this.isServiceRun) {
      this.editForm.get('subDispositionId')?.clearValidators();
      this.editForm.get('subDispositionId')?.updateValueAndValidity();
      return;
    }

    if (this.appointment?.dispositionId) {
      this.updateSubDispositions(this.appointment.dispositionId);
    }

    this.editForm.get('dispositionId')?.valueChanges.subscribe((dispositionId) => {
      this.updateSubDispositions(dispositionId ?? '');
      this.editForm.patchValue({ subDispositionId: '' });
    });
  }

  fetchDispositions(): void {
    this.loadingDispositions.set(true);
    this.loadDispositionsError.set(null);

    if (this.isServiceRun) {
      this.dispositionService.getServiceAppointmentDispositions().subscribe({
        next: (dispositions) => {
          this.dispositions.set(
            (dispositions ?? []).map((d) => new AppointmentDispositionItemInfo({ ...d, category: undefined, subDispositions: undefined }))
          );
          this.loadingDispositions.set(false);

          if (this.appointment?.dispositionId) {
            this.editForm.patchValue({ dispositionId: this.appointment.dispositionId });
          }
        },
        error: (error) => {
          console.error('Error loading service dispositions:', error);
          this.loadDispositionsError.set('Unable to load dispositions.');
          this.loadingDispositions.set(false);
        },
      });
      return;
    }

    this.dispositionService.getAppointmentDispositions().subscribe({
      next: (dispositions) => {
        const filteredDispositions = (dispositions ?? []).filter(
          (disposition) => disposition.name?.toLowerCase() !== 'sold'
        );
        this.dispositions.set(filteredDispositions);
        this.loadingDispositions.set(false);

        if (this.appointment?.dispositionId && this.appointment?.subDispositionId) {
          this.editForm.patchValue({
            dispositionId: this.appointment.dispositionId,
            subDispositionId: this.appointment.subDispositionId,
          });
        }
      },
      error: (error) => {
        console.error('Error loading dispositions:', error);
        this.loadDispositionsError.set('Unable to load dispositions.');
        this.loadingDispositions.set(false);
      },
    });
  }

  updateSubDispositions(dispositionId: string): void {
    const selectedDisposition = this.dispositions().find((d) => d.id === dispositionId);
    this.subDispositions.set(selectedDisposition?.subDispositions ?? []);
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  onSave(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.updating.set(true);
    this.updateError.set(null);

    const dispositionId = this.editForm.value.dispositionId!;

    const update$: Observable<unknown> = this.isServiceRun
      ? this.dispositionService.updateServiceAppointmentDisposition(
          new UpdateServiceAppointmentDispositionSubmissionDto({
            serviceAppointmentId: this.appointment!.id!,
            opportunityId: this.appointment!.opportunityId!,
            dispositionId,
            dispositionDateEntered: new Date(),
          })
        )
      : this.dispositionService.updateAppointmentDisposition(
          new CreateAppointmentDispositionUpdateSubmissionDto({
            appointmentId: this.appointment!.id!,
            opportunityId: this.appointment!.opportunityId!,
            dispositionId,
            subDispositionId: this.editForm.value.subDispositionId!,
            dispositionDateEntered: new Date(),
          })
        );

    update$.subscribe({
      next: () => {
        this.updating.set(false);
        this.activeModal.close(true);
      },
      error: (error) => {
        this.updating.set(false);
        console.error('Update error:', error);
        this.updateError.set(
          error?.message ?? 'Failed to update disposition. Please try again.'
        );
      },
    });
  }

  getDispositionName(dispositionId: string): string {
    return this.dispositions().find((d) => d.id === dispositionId)?.name ?? '';
  }

  getSubDispositionName(subDispositionId: string): string {
    return this.subDispositions().find((s) => s.id === subDispositionId)?.name ?? '';
  }
}
